import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  HStack,
  Stack,
  Button,
  useToast 
} from '@chakra-ui/react';
import { getVideoData, updateVideoData } from '../../../services/Videos';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import FormLoader from '../../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';

const Keywords = () => {

  const { alias } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'keywords', 'keywords1_alias', 'keywords2_alias', 'keywords3_alias', 'keywords4_alias', 'keywords5_alias', 'keywords6_alias', 'keywords7_alias', 'keywords8_alias', 'keywords9_alias', 'keywords10_alias', 'keywords11_alias', 'keywords12_alias', 'keywords13_alias', 'keywords14_alias', 'keywords15_alias', 'other_keywords_alias'];
    const rightFields = ['essential_reading_for_keyword1', 'essential_reading_for_keyword2', 'suggested_reading_for_keyword1', 'suggested_reading_for_keyword2', 'precise_pagination_for_keyword1', 'precise_pagination_for_keyword2', 'precise_pagination_for_keyword3', 'precise_pagination_for_keyword4', 'precise_pagination_for_keyword5', 'precise_pagination_for_keyword6', 'precise_pagination_for_keyword7', 'precise_pagination_for_keyword8', 'precise_pagination_for_keyword9', 'precise_pagination_for_keyword10', 'precise_pagination_for_keyword11', 'precise_pagination_for_keyword12', 'precise_pagination_for_keyword13', 'precise_pagination_for_keyword14'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "keywords": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Keywords"
      },
      "keywords1_alias": {
        "type": "string",
        "title": "Keywords 1 Alias"
      },
      "keywords2_alias": {
        "type": "string",
        "title": "Keywords 2 Alias"
      },
      "keywords3_alias": {
        "type": "string",
        "title": "Keywords 3 Alias"
      },
      "keywords4_alias": {
        "type": "string",
        "title": "Keywords 4 Alias"
      },
      "keywords5_alias": {
        "type": "string",
        "title": "Keywords 5 Alias"
      },
      "keywords6_alias": {
        "type": "string",
        "title": "Keywords 6 Alias"
      },
      "keywords7_alias": {
        "type": "string",
        "title": "Keywords 7 Alias"
      },
      "keywords8_alias": {
        "type": "string",
        "title": "Keywords 8 Alias"
      },
      "keywords9_alias": {
        "type": "string",
        "title": "Keywords 9 Alias"
      },
      "keywords10_alias": {
        "type": "string",
        "title": "Keywords 10 Alias"
      },
      "keywords11_alias": {
        "type": "string",
        "title": "Keywords 11 Alias"
      },
      "keywords12_alias": {
        "type": "string",
        "title": "Keywords 12 Alias"
      },
      "keywords13_alias": {
        "type": "string",
        "title": "Keywords 13 Alias"
      },
      "keywords14_alias": {
        "type": "string",
        "title": "Keywords 14 Alias"
      },
      "keywords15_alias": {
        "type": "string",
        "title": "Keywords 15 Alias"
      },
      "other_keywords_alias": {
        "type": "string",
        "title": "Other Keywords Alias"
      }
    }
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "essential_reading_for_keyword1": {
        "type": "string",
        "title": "Essential Reading for Keyword 1"
      },
      "essential_reading_for_keyword2": {
        "type": "string",
        "title": "Essential Reading for Keyword 2"
      },
      "suggested_reading_for_keyword1": {
        "type": "string",
        "title": "Suggested Reading for Keyword 1"
      },
      "suggested_reading_for_keyword2": {
        "type": "string",
        "title": "Suggested Reading for Keyword 2"
      },
      "precise_pagination_for_keyword1": {
        "type": "string",
        "title": "Precise Pagination for Keyword 1"
      },
      "precise_pagination_for_keyword2": {
        "type": "string",
        "title": "Precise Pagination for Keyword 2"
      },
      "precise_pagination_for_keyword3": {
        "type": "string",
        "title": "Precise Pagination for Keyword 3"
      },
      "precise_pagination_for_keyword4": {
        "type": "string",
        "title": "Precise Pagination for Keyword 4"
      },
      "precise_pagination_for_keyword5": {
        "type": "string",
        "title": "Precise Pagination for Keyword 5"
      },
      "precise_pagination_for_keyword6": {
        "type": "string",
        "title": "Precise Pagination for Keyword 6"
      },
      "precise_pagination_for_keyword7": {
        "type": "string",
        "title": "Precise Pagination for Keyword 7"
      },
      "precise_pagination_for_keyword8": {
        "type": "string",
        "title": "Precise Pagination for Keyword 8"
      },
      "precise_pagination_for_keyword9": {
        "type": "string",
        "title": "Precise Pagination for Keyword 9"
      },
      "precise_pagination_for_keyword10": {
        "type": "string",
        "title": "Precise Pagination for Keyword 10"
      },
      "precise_pagination_for_keyword11": {
        "type": "string",
        "title": "Precise Pagination for Keyword 11"
      },
      "precise_pagination_for_keyword12": {
        "type": "string",
        "title": "Precise Pagination for Keyword 12"
      },
      "precise_pagination_for_keyword13": {
        "type": "string",
        "title": "Precise Pagination for Keyword 13"
      },
      "precise_pagination_for_keyword14": {
        "type": "string",
        "title": "Precise Pagination for Keyword 14"
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
    "title": "Keywords Fields",
    "description": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "keywords": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Keywords"
      },
      "keywords1_alias": {
        "type": "string",
        "title": "Keywords 1 Alias"
      },
      "keywords2_alias": {
        "type": "string",
        "title": "Keywords 2 Alias"
      },
      "keywords3_alias": {
        "type": "string",
        "title": "Keywords 3 Alias"
      },
      "keywords4_alias": {
        "type": "string",
        "title": "Keywords 4 Alias"
      },
      "keywords5_alias": {
        "type": "string",
          "title": "Keywords 5 Alias"
      },
      "keywords6_alias": {
        "type": "string",
        "title": "Keywords 6 Alias"
      },
      "keywords7_alias": {
        "type": "string",
        "title": "Keywords 7 Alias"
      },
      "keywords8_alias": {
        "type": "string",
        "title": "Keywords 8 Alias"
      },
      "keywords9_alias": {
        "type": "string",
        "title": "Keywords 9 Alias"
      },
      "keywords10_alias": {
        "type": "string",
        "title": "Keywords 10 Alias"
      },
      "keywords11_alias": {
        "type": "string",
        "title": "Keywords 11 Alias"
      },
      "keywords12_alias": {
        "type": "string",
        "title": "Keywords 12 Alias"
      },
      "keywords13_alias": {
        "type": "string",
        "title": "Keywords 13 Alias"
      },
      "keywords14_alias": {
        "type": "string",
        "title": "Keywords 14 Alias"
      },
      "keywords15_alias": {
        "type": "string",
        "title": "Keywords 15 Alias"
      },
      "other_keywords_alias": {
        "type": "string",
        "title": "Other Keywords Alias"
      },
      "essential_reading_for_keyword1": {
        "type": "string",
        "title": "Essential Reading for Keyword 1"
      },
      "essential_reading_for_keyword2": {
        "type": "string",
        "title": "Essential Reading for Keyword 2"
      },
      "suggested_reading_for_keyword1": {
        "type": "string",
        "title": "Suggested Reading for Keyword 1"
      },
      "suggested_reading_for_keyword2": {
        "type": "string",
        "title": "Suggested Reading for Keyword 2"
      },
      "precise_pagination_for_keyword1": {
        "type": "string",
        "title": "Precise Pagination for Keyword 1"
      },
      "precise_pagination_for_keyword2": {
        "type": "string",
        "title": "Precise Pagination for Keyword 2"
      },
      "precise_pagination_for_keyword3": {
        "type": "string",
        "title": "Precise Pagination for Keyword 3"
      },
      "precise_pagination_for_keyword4": {
        "type": "string",
        "title": "Precise Pagination for Keyword 4"
      },
      "precise_pagination_for_keyword5": {
        "type": "string",
        "title": "Precise Pagination for Keyword 5"
      },
      "precise_pagination_for_keyword6": {
        "type": "string",
        "title": "Precise Pagination for Keyword 6"
      },
      "precise_pagination_for_keyword7": {
        "type": "string",
        "title": "Precise Pagination for Keyword 7"
      },
      "precise_pagination_for_keyword8": {
        "type": "string",
        "title": "Precise Pagination for Keyword 8"
      },
      "precise_pagination_for_keyword9": {
        "type": "string",
        "title": "Precise Pagination for Keyword 9"
      },
      "precise_pagination_for_keyword10": {
        "type": "string",
        "title": "Precise Pagination for Keyword 10"
      },
      "precise_pagination_for_keyword11": {
        "type": "string",
        "title": "Precise Pagination for Keyword 11"
      },
      "precise_pagination_for_keyword12": {
        "type": "string",
        "title": "Precise Pagination for Keyword 12"
      },
      "precise_pagination_for_keyword13": {
        "type": "string",
        "title": "Precise Pagination for Keyword 13"
      },
      "precise_pagination_for_keyword14": {
        "type": "string",
        "title": "Precise Pagination for Keyword 14"
      }
    }
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "keywords": {
      "ui:readonly": true,
      "ui:options": {
        "addable": false,
        "removable": false,
        "orderable": false
      }
    },
    "keywords1_alias": { "ui:emptyValue": "" },
    "keywords2_alias": { "ui:emptyValue": "" },
    "keywords3_alias": { "ui:emptyValue": "" },
    "keywords4_alias": { "ui:emptyValue": "" },
    "keywords5_alias": { "ui:emptyValue": "" },
    "keywords6_alias": { "ui:emptyValue": "" },
    "keywords7_alias": { "ui:emptyValue": "" },
    "keywords8_alias": { "ui:emptyValue": "" },
    "keywords9_alias": { "ui:emptyValue": "" },
    "keywords10_alias": { "ui:emptyValue": "" },
    "keywords11_alias": { "ui:emptyValue": "" },
    "keywords12_alias": { "ui:emptyValue": "" },
    "keywords13_alias": { "ui:emptyValue": "" },
    "keywords14_alias": { "ui:emptyValue": "" },
    "keywords15_alias": { "ui:emptyValue": "" },
    "other_keywords_alias": { "ui:emptyValue": "" }
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
    },
    "essential_reading_for_keyword1": { "ui:emptyValue": "" },
    "essential_reading_for_keyword2": { "ui:emptyValue": "" },
    "suggested_reading_for_keyword1": { "ui:emptyValue": "" },
    "suggested_reading_for_keyword2": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword1": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword2": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword3": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword4": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword5": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword6": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword7": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword8": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword9": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword10": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword11": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword12": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword13": { "ui:emptyValue": "" },
    "precise_pagination_for_keyword14": { "ui:emptyValue": "" }
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
      "keywords": {
      "ui:readonly": true,
        "ui:options": {
        "addable": false,
        "removable": false,
        "orderable": false
        }
      },
      "keywords1_alias": { "ui:emptyValue": "" },
      "keywords2_alias": { "ui:emptyValue": "" },
      "keywords3_alias": { "ui:emptyValue": "" },
      "keywords4_alias": { "ui:emptyValue": "" },
      "keywords5_alias": { "ui:emptyValue": "" },
      "keywords6_alias": { "ui:emptyValue": "" },
      "keywords7_alias": { "ui:emptyValue": "" },
      "keywords8_alias": { "ui:emptyValue": "" },
      "keywords9_alias": { "ui:emptyValue": "" },
      "keywords10_alias": { "ui:emptyValue": "" },
      "keywords11_alias": { "ui:emptyValue": "" },
      "keywords12_alias": { "ui:emptyValue": "" },
      "keywords13_alias": { "ui:emptyValue": "" },
      "keywords14_alias": { "ui:emptyValue": "" },
      "keywords15_alias": { "ui:emptyValue": "" },
      "other_keywords_alias": { "ui:emptyValue": "" },
      "essential_reading_for_keyword1": { "ui:emptyValue": "" },
      "essential_reading_for_keyword2": { "ui:emptyValue": "" },
      "suggested_reading_for_keyword1": { "ui:emptyValue": "" },
      "suggested_reading_for_keyword2": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword1": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword2": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword3": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword4": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword5": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword6": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword7": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword8": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword9": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword10": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword11": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword12": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword13": { "ui:emptyValue": "" },
      "precise_pagination_for_keyword14": { "ui:emptyValue": "" }
    };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getVideoData(alias, 'keywords');

          if (result) {
            const data = result?.data?.[0] || {};
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }
      
      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateVideoData(alias, finalFormData);
        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">Archive Keywords Fields</Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">Archive Keywords Fields</Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
          <Form 
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
        validator={validator}  
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Keywords;